"use client";

import { ExpandableToggle, type ToggleOption } from "@/components/common/expandable-toggle";
import { useCurrentCityName } from "@/hooks/use-current-city";
import { useSearchMode } from "@/hooks/use-search-mode";
import { ListFilterPlus, WandSparkles } from "lucide-react";
import { useRouter } from "next/navigation";
import { useCallback, useEffect } from "react";

const modeOptions: ToggleOption<"manual" | "preference">[] = [
	{ value: "manual", label: "Search", icon: <ListFilterPlus className="w-3.5 h-3.5" style={{ strokeWidth: 2 }} /> },
	{
		value: "preference",
		label: "Preferences",
		icon: <WandSparkles className="w-3.5 h-3.5" style={{ strokeWidth: 2 }} />,
	},
];

interface SearchModeToggleProps {
	onModeChange?: (mode: "manual" | "preference") => void;
}

export function SearchModeToggle({ onModeChange }: SearchModeToggleProps) {
	const { mode: activeMode, setMode, initializeFromUrl } = useSearchMode();
	const currentCity = useCurrentCityName();
	const router = useRouter();

	// Initialize mode from URL when component mounts
	useEffect(() => {
		initializeFromUrl();
	}, [initializeFromUrl]);

	const handleModeChange = useCallback(
		(newMode: "manual" | "preference") => {
			setMode(newMode);
			onModeChange?.(newMode);

			// Handle navigation based on the new mode
			if (newMode === "preference") {
				router.push("/preference");
			} else {
				router.push(`/${currentCity}`);
			}
		},
		[setMode, onModeChange, router, currentCity],
	);

	return (
		<ExpandableToggle
			options={modeOptions}
			value={activeMode}
			onChange={handleModeChange}
			switchCollapseDelay={1000}
			className="border-neutral-200 bg-white"
		/>
	);
}
